import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  loadAccountScope, resolveAccountScope, saveAccountScope,
} from "@/lib/accountScope";

const AccountScopeContext = createContext(null);

/**
 * The user's accounts and the account every money view is narrowed to.
 *
 * Accounts load once here rather than on each page, so the header selector,
 * the transaction form and Settings all read the same list, and an edit in
 * Settings shows up in the selector straight away.
 */
export const AccountScopeProvider = ({ children }) => {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [ready, setReady] = useState(false);
  const [savedScope, setSavedScope] = useState(loadAccountScope);

  const reloadAccounts = useCallback(async () => {
    const response = await api.get("/accounts");
    setAccounts(response.data);
    setReady(true);
    return response.data;
  }, []);

  useEffect(() => {
    if (!user) return;
    reloadAccounts().catch(() => setReady(true));
  }, [user, reloadAccounts]);

  const setScope = useCallback((next) => {
    setSavedScope(next);
    saveAccountScope(next);
  }, []);

  const value = useMemo(() => {
    // Until the list arrives the saved scope is trusted as-is; once it does,
    // an account deleted since it was saved falls back to all accounts.
    const scope = ready ? resolveAccountScope(savedScope, accounts) : savedScope;
    const accountsById = Object.fromEntries(accounts.map((account) => [account.id, account]));
    return {
      accounts,
      accountsById,
      ready,
      scope,
      selectedAccount: accountsById[scope] || null,
      setScope,
      reloadAccounts,
    };
  }, [accounts, ready, savedScope, setScope, reloadAccounts]);

  return (
    <AccountScopeContext.Provider value={value}>
      {children}
    </AccountScopeContext.Provider>
  );
};

export const useAccountScope = () => useContext(AccountScopeContext);
